import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, UserPlus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { getCurrencySymbol } from "@/lib/currencyUtils";
import {
  computeGroupLeaderImpact,
  type LeaderSourceItem,
} from "@/lib/groupLeaderUtils";

interface Props {
  leadId: string;
  items: LeaderSourceItem[];
  currency?: string;
  /** Paying travellers (excluding leaders) */
  payingPax: number;
  savedLeaderCount?: number | null;
  savedRoomOverrides?: Record<string, number>;
  disabled?: boolean;
}

export function GroupLeaderDialog({
  leadId,
  items,
  currency = "ZAR",
  payingPax,
  savedLeaderCount = null,
  savedRoomOverrides = {},
  disabled = false,
}: Props) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState((savedLeaderCount || 0) > 0);
  const [count, setCount] = useState(String(savedLeaderCount || 1));
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const symbol = getCurrencySymbol(currency);
  const activeCount = enabled ? Math.max(1, parseInt(count) || 1) : 0;

  useEffect(() => {
    if (!open) return;
    setEnabled((savedLeaderCount || 0) > 0);
    setCount(String(savedLeaderCount || 1));
    setOverrides(
      Object.fromEntries(
        Object.entries(savedRoomOverrides || {}).map(([k, v]) => [k, String(v)])
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const numericOverrides = useMemo(() => {
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(overrides)) {
      if (v === "") continue;
      const n = parseFloat(v);
      if (!isNaN(n)) out[k] = n;
    }
    return out;
  }, [overrides]);

  const impact = useMemo(
    () =>
      computeGroupLeaderImpact(items, {
        payingPax,
        leaderCount: activeCount,
        roomOverrides: numericOverrides,
      }),
    [items, payingPax, activeCount, numericOverrides]
  );

  const format = (v: number) =>
    `${symbol}${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const perPersonAdd = payingPax > 0 ? impact.netTotal / payingPax : 0;

  const persist = async () => {
    setSaving(true);
    const { error } = await supabase
      .from("leads")
      .update({
        group_leader_count: activeCount > 0 ? activeCount : null,
        group_leader_room_overrides: activeCount > 0 && Object.keys(numericOverrides).length
          ? numericOverrides
          : null,
      } as any)
      .eq("id", leadId);
    setSaving(false);
    if (error) {
      toast.error("Could not save the group leader setting");
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
    queryClient.invalidateQueries({ queryKey: ["lead-supplement-context", leadId] });
    queryClient.invalidateQueries({ queryKey: ["lead-group-leader", leadId] });
    toast.success(
      activeCount > 0
        ? "Group leader added — cost folded into the per person price"
        : "Group leader removed"
    );
    setOpen(false);
  };

  const removeLeader = async () => {
    setSaving(true);
    const { error } = await supabase
      .from("leads")
      .update({
        group_leader_count: null,
        group_leader_room_overrides: null,
      } as any)
      .eq("id", leadId);
    setSaving(false);
    if (error) {
      toast.error("Could not remove the group leader");
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
    queryClient.invalidateQueries({ queryKey: ["lead-supplement-context", leadId] });
    queryClient.invalidateQueries({ queryKey: ["lead-group-leader", leadId] });
    setEnabled(false);
    setCount("1");
    setOverrides({});
    toast.success("Group leader removed — figures restored");
    setOpen(false);
  };


  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" disabled={disabled}>
          <UserPlus className="h-4 w-4" />
          Group leader
          {(savedLeaderCount || 0) > 0 && (
            <Badge variant="secondary" className="ml-1">
              {savedLeaderCount}
            </Badge>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add a group leader</DialogTitle>
          <DialogDescription>
            The leader travels free of charge — their cost is folded into the price per paying
            traveller. Nothing on the programme items is changed, so you can switch this off at
            any time.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4 p-3 rounded-lg border bg-muted/30">
            <div className="flex items-center gap-3">
              <Switch id="leader-enabled" checked={enabled} onCheckedChange={setEnabled} />
              <Label htmlFor="leader-enabled" className="cursor-pointer">
                Include a group leader
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="leader-count" className="text-xs text-muted-foreground">
                How many
              </Label>
              <Input
                id="leader-count"
                type="number"
                min={1}
                max={10}
                value={count}
                disabled={!enabled}
                onChange={(e) => setCount(e.target.value)}
                className="h-8 w-20"
              />
            </div>
          </div>

          <div className="text-xs text-muted-foreground">
            Paying travellers: <span className="font-medium text-foreground">{payingPax}</span>
            {activeCount > 0 && (
              <>
                {" "}· costed for{" "}
                <span className="font-medium text-foreground">{payingPax + activeCount}</span> people
              </>
            )}
          </div>

          {activeCount > 0 && impact.roomLines.length > 0 && (
            <div className="space-y-2 max-h-[35vh] overflow-y-auto">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Extra room per property
              </p>
              {impact.roomLines.map((line) => (
                <div
                  key={line.itemId}
                  className="flex items-center gap-2 p-2 rounded border bg-background text-xs"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{line.label}</div>
                    <div className="text-muted-foreground">
                      {line.rooms} room{line.rooms === 1 ? "" : "s"} × {line.nights}{" "}
                      {line.nights === 1 ? "night" : "nights"} × {format(line.perNight)}/night
                    </div>
                  </div>
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    value={overrides[line.itemId] ?? line.total.toFixed(2)}
                    onChange={(e) =>
                      setOverrides((prev) => ({ ...prev, [line.itemId]: e.target.value }))
                    }
                    className="h-8 w-28 text-right"
                  />
                </div>
              ))}
            </div>
          )}

          {activeCount > 0 && (
            <div className="p-3 rounded-lg border bg-muted/40 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Extra accommodation</span>
                <span>{format(impact.accommodationTotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Activities &amp; ground handling</span>
                <span>{format(impact.otherTotal)}</span>
              </div>
              <div className="flex justify-between text-sm pt-1 border-t">
                <span className="font-semibold">Leader cost absorbed</span>
                <span className="font-bold text-primary">{format(impact.netTotal)}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Added to each paying traveller (before markup)</span>
                <span>{format(perPersonAdd)}</span>
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            The leader is always an extra person on top of the paying travellers — the paying count
            is never changed.
          </p>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {(savedLeaderCount || 0) > 0 ? (
            <Button
              type="button"
              variant="outline"
              onClick={removeLeader}
              disabled={saving}
              className="text-destructive"
            >
              Remove group leader
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="button" onClick={persist} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
